import React, { useCallback, useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import DatePicker from "react-datepicker";
import { yupResolver } from "@hookform/resolvers/yup";
import Select from "react-select";
import TimePicker from "react-times";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import "react-times/css/material/default.css";

import SWInterface from "../../Components/Layout/SWInterface";
import ModelContainer from "../../Components/ModelContainer";
import {
  changeTaxEndApi,
  checkTaxYear,
  getSetupDetails,
  getTaxYearEndDate,
  saveAccountingPeriod,
  saveSetupWizardForm,
  showNotificationWithTimeout,
  toggleSettingAccModal,
} from "../../../Redux/actions";
import ErrorContainer from "../../Components/ErrorContainer";
import moment from "moment";
import { capitalizeFirstLetter, parseStringToDate } from "../../../helpers";
import BtmButtons from "../../Components/BtmButtons";

const schema = yup
  .object({
    is_first_period: yup.number().required(),
    ap_id: yup.string(),
    tax_year: yup.string().required(),
    account_period_setting_id: yup.string().required(),
    sub_period: yup.string().required(),
    day_of_week: yup.string().when("account_period_setting_id", {
      is: (period_setting) => period_setting != 4,
      then: yup.string().required(),
    }),
    start_date: yup.string().required(),
    end_date: yup.string().required(),
  })
  .required();

const SetupWizardThree = (params) => {
  const initialAccForm = {
    is_first_period: 1,
    ap_id: "",
    tax_year: "",
    account_period_setting_id: "",
    sub_period: "",
    day_of_week: "",
    start_date: "",
    end_date: "",
  };

  const initialDayTime = {
    id: "",
    start_time: "05:00 AM",
    end_time: "04:59 AM",
    is_checked: true,
  };

  const initialScheduleDayForm = {
    monday: initialDayTime,
    tuesday: initialDayTime,
    wednesday: initialDayTime,
    thursday: initialDayTime,
    friday: initialDayTime,
    saturday: initialDayTime,
    sunday: initialDayTime,
  };
  const {
    register,
    formState: { errors: accErrors },
    handleSubmit: accountingSubmit,
    control,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialAccForm,
    mode: "all",
    reValidateMode: "onChange",
  });

  const history = useHistory();
  const dispatch = useDispatch();
  const {
    account,
    accountingPeriods,
    tenYearsBetween,
    accountPeriodSettings,
    sub_period,
    settingTypes,
    dayNames,
    scheduledDays,
  } = useSelector((state) => state.authReducer.setup_wizard_details);

  const {
    tax_year_res,
    tax_end_date_res,
    tax_end_date_start_from,
    run_tax_end_date_api: taxEndApi,
  } = useSelector((state) => state.authReducer);

  const { is_loading, is_modal_loading, accountingModal } = useSelector(
    (state) => state.settingReducer
  );

  const [isNewBalanceSheet, setIsNewBalanceSheet] = useState(1);
  const [accInputForm, setAccInputForm] = useState(initialAccForm);
  const [scheduleDayForm, setScheduleDayForm] = useState(
    initialScheduleDayForm
  );

  const isLogoutRef = useRef(false);
  const submitBtnRef = useRef(null);

  const changeScheduleDayHandler = (input, type, val) => {
    const prevValue = { ...scheduleDayForm[input], [type]: val };
    setScheduleDayForm((prevState) => ({ ...prevState, [input]: prevValue }));
  };

  const changeAccInputHandler = (input, val) => {
    setValue(input, val);
    setAccInputForm((prevState) => ({ ...prevState, [input]: val }));
    if (
      (input == "start_date" && val !== "") ||
      (input != "start_date" &&
        accInputForm?.start_date !== "" &&
        (input == "tax_year" || input == "sub_period"))
    ) {
      onChangeOfTaxYear();
    }
  };

  useEffect(() => {
    if (tax_end_date_res?.return == 1 && tax_end_date_res?.data) {
      if (tax_end_date_start_from) {
        changeAccInputHandler(
          "end_date",
          moment(tax_end_date_res?.data).toDate()
        );
      } else {
        changeAccInputHandler(
          "start_date",
          moment(tax_end_date_res?.data).toDate()
        );
      }
    } else if (tax_end_date_res?.return == 0) {
      changeAccInputHandler("end_date", "");
      changeAccInputHandler("start_date", "");
      if (tax_end_date_res?.message) {
        dispatch(
          showNotificationWithTimeout(`${tax_end_date_res?.message}`, "error")
        );
      }
    }
  }, [tax_end_date_res]);

  const initialModalState = () => {
    setAccInputForm(initialAccForm);
    reset(initialAccForm);
    changeTaxEndApi({
      run_tax_end_date_api: null,
      tax_end_date_res: null,
    });
  };

  useEffect(() => {
    dispatch(getSetupDetails(2));
    dispatch(
      changeTaxEndApi({
        run_tax_end_date_api: null,
        tax_end_date_res: null,
      })
    );
  }, []);

  useEffect(() => {
    if (taxEndApi !== null)
      dispatch(
        getTaxYearEndDate({
          ...accInputForm,
          start_date:
            accInputForm?.start_date && accInputForm?.start_date !== ""
              ? moment(accInputForm?.start_date).format("yyyy-MM-DD")
              : "",
          end_date:
            accInputForm?.end_date && accInputForm?.end_date !== ""
              ? moment(accInputForm?.end_date).format("yyyy-MM-DD")
              : "",
        })
      );
  }, [taxEndApi]);

  useEffect(() => {
    if (accountingPeriods && accountingPeriods.length > 0) {
      changeAccInputHandler("is_first_period", 0);
    }
    if (account?.merchant?.is_new_balance_sheet) {
      setIsNewBalanceSheet(account?.merchant?.is_new_balance_sheet);
    }
  }, [account]);

  useEffect(() => {
    if (scheduledDays && scheduledDays.length > 0) {
      const newScheduleDayObj = { ...initialScheduleDayForm };
      Object.keys(newScheduleDayObj).map((input) => {
        const index = scheduledDays.findIndex((p) => p.weekday == input);
        if (index > -1) {
          const scheduledDay = scheduledDays[index];
          newScheduleDayObj[input] = {
            id: scheduledDay?.id,
            start_time: parseStringToDate(
              scheduledDay?.start_time,
              "HH:mm:ss",
              "hh:mm A"
            ),
            end_time: parseStringToDate(
              scheduledDay?.end_time,
              "HH:mm:ss",
              "hh:mm A"
            ),
            is_checked: true,
          };
        } else {
          newScheduleDayObj[input] = {
            id: "",
            start_time: "05:00 AM",
            end_time: "04:59 AM",
            is_checked: false,
          };
        }
      });
      console.log("newScheduleDayObj", newScheduleDayObj);
      setScheduleDayForm(newScheduleDayObj);
    }
  }, [scheduledDays]);

  const onChangeOfTaxYear = () => {
    if (accInputForm?.is_first_period == 0) {
      dispatch(
        checkTaxYear({
          tax_year: accInputForm?.tax_year,
        })
      );
    } else {
      dispatch(
        changeTaxEndApi({
          run_tax_end_date_api: taxEndApi == null ? true : !taxEndApi,
        })
      );
    }
  };

  const closeModal = useCallback(
    (value) => {
      dispatch(toggleSettingAccModal(value));
      initialModalState();
    },
    [accountingModal]
  );

  const editAccountingHandler = (period) => {
    closeModal(true);
    const editValues = {
      is_first_period: accInputForm?.is_first_period,
      ap_id: period?.id ?? "",
      tax_year: period?.tax_year ?? "",
      account_period_setting_id: period?.account_period_setting_id ?? "",
      sub_period: period?.sub_period ?? "",
      day_of_week: period?.day_of_week ?? "",
      start_date: period?.start_date ? moment(period?.start_date).toDate() : "",
      end_date: period?.end_date ? moment(period?.end_date).toDate() : "",
    };
    setAccInputForm(editValues);
    reset(editValues);
  };

  const submitAccountingForm = (data) => {
    console.log("submitAccountingForm", data);
    data.start_date = moment(data?.start_date).format("yyyy-MM-DD");
    data.end_date = moment(data?.end_date).format("yyyy-MM-DD");
    dispatch(saveAccountingPeriod(data));
    // closeModal(false);
  };

  const submitScheduleDayForm = (event) => {
    event.preventDefault();
    let scheduleDayArr = [];
    Object.keys(scheduleDayForm)
      .filter((p) => {
        return scheduleDayForm[p].is_checked == true;
      })
      .map((input) => {
        const inputObj = {
          weekday: input,
          start_time: parseStringToDate(
            scheduleDayForm[input].start_time,
            "hh:mm A",
            "HH:mm:ss"
          ),
          end_time: parseStringToDate(
            scheduleDayForm[input].end_time,
            "hh:mm A",
            "HH:mm:ss"
          ),
        };
        if (scheduleDayForm[input].id !== "") {
          inputObj.id = scheduleDayForm[input].id;
        }
        scheduleDayArr.push(inputObj);
      });
    console.log("scheduleDayArr", scheduleDayArr);
    dispatch(
      saveSetupWizardForm(
        "two",
        { scheduled_days: scheduleDayArr },
        isLogoutRef?.current
      )
    );
  };

  const submitNextHandler = (isLogoutType = false) => {
    isLogoutRef.current = isLogoutType;
    if (submitBtnRef !== null) {
      submitBtnRef.current.click();
    }
  };

  return (
    <SWInterface step={2}>
      <div className="steps-content row_count2">
        <div className="steps-div_outer">
          <div className="steps-div d-block">
            <form id="form_step_two" onSubmit={submitScheduleDayForm}>
              <h4 className="event-alert mb-0"> Business Settings </h4>
              <p class="mt-0">
                {" "}
                Take this opportunity to setup some of your business’ core
                Finance and Operations settings to help allow GoGMGo work best
                for you.{" "}
              </p>
              <div class="row-fluid other_input">
                {(account?.merchant?.parent_merchant_id > 0 &&
                  isNewBalanceSheet == 1) ||
                  (account?.merchant?.parent_merchant_id == 0 && (
                    <>
                      <h4 class="sm-hd"> Accounting Periods </h4>
                      <p class="mt-0">
                        {" "}
                        These periods will represent the financial years for
                        this company, you can start with the current Tax year
                        and add more once in the core system.{" "}
                      </p>

                      <div class="table-responsive inner-space screen-scroll">
                        <table class="table checklist-table remove-space inventory_table">
                          <thead>
                            <tr>
                              <th> Tax Year </th>
                              <th> Accounting Period Setting </th>
                              <th> Day Of Week </th>
                              <th> Period Start </th>
                              <th> Period End </th>
                              <th> Actions </th>
                            </tr>
                          </thead>
                          <tbody>
                            {accountingPeriods &&
                              accountingPeriods?.length > 0 &&
                              accountingPeriods.map((period) => {
                                return (
                                  <tr>
                                    <td> {period?.tax_year ?? "-"} </td>
                                    <td>
                                      {period?.account_period_setting?.name ??
                                        "-"}
                                    </td>
                                    <td>
                                      {" "}
                                      {period?.sub_period
                                        ? capitalizeFirstLetter(
                                            period?.sub_period
                                          )
                                        : "-"}{" "}
                                    </td>
                                    <td>
                                      {period?.start_date
                                        ? parseStringToDate(
                                            period?.start_date,
                                            "yyyy-MM-DD",
                                            "MMMM DD, yyyy"
                                          )
                                        : "-"}
                                    </td>
                                    <td>
                                      {" "}
                                      {period?.end_date
                                        ? parseStringToDate(
                                            period?.end_date,
                                            "yyyy-MM-DD",
                                            "MMMM DD, yyyy"
                                          )
                                        : "-"}{" "}
                                    </td>
                                    <td>
                                      {" "}
                                      <a
                                        class="circle_icon editAp"
                                        onClick={() =>
                                          editAccountingHandler(period)
                                        }
                                      >
                                        <i class="iconSprites edit-icon">
                                          <div class="tooltip-sm"> Edit </div>
                                        </i>
                                      </a>{" "}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                      <div class="my-3">
                        <a
                          href="javascript:void(0)"
                          onClick={() => closeModal(true)}
                          class="btn green-btn-revert add-plus "
                        >
                          <i class="i-icon"> </i> Add Tax Year{" "}
                        </a>
                      </div>
                    </>
                  ))}
                <h4 class="sm-hd mt-3"> Operating Hours </h4>
                <p class="mt-0">
                  {" "}
                  Please indicate the days of the week and hours for each day
                  where operational. If you expect to ever be open on a given
                  day of the week, even if irregularly, please tick that day to
                  ensure it is included and you can always choose to exclude in
                  the future.
                </p>

                <div class="operating_hours col-md-7">
                  <table class="table checklist-table" border="0">
                    <thead>
                      <tr>
                        <th> </th>
                        <th> Day </th>
                        <th className="col-md-4"> From </th>
                        <th className="col-md-4"> To </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dayNames &&
                        dayNames !== null &&
                        Object.keys(dayNames).map((index, key) => {
                          return (
                            <tr key={index}>
                              <td>
                                <div class="checkbox avaiCheck">
                                  <input
                                    type="checkbox"
                                    className="avail_checkbox"
                                    id={`checkbox_index_${key}`}
                                    checked={scheduleDayForm[index].is_checked}
                                    onClick={() => {
                                      changeScheduleDayHandler(
                                        index,
                                        "is_checked",
                                        !scheduleDayForm[index].is_checked
                                      );
                                    }}
                                  />
                                  <label for={`checkbox_index_${key}`}></label>
                                </div>
                              </td>
                              <td> {dayNames?.[index]} </td>
                              <td>
                                <div className="timePicker">
                                  <TimePicker
                                    timeMode="12"
                                    minuteStep={30}
                                    time={scheduleDayForm[index].start_time}
                                    onTimeChange={(options) => {
                                      const { hour, minute, meridiem } =
                                        options;
                                      changeScheduleDayHandler(
                                        index,
                                        "start_time",
                                        `${hour}:${minute} ${meridiem}`
                                      );
                                    }}
                                  />
                                </div>
                              </td>
                              <td>
                                <div className="timePicker">
                                  <TimePicker
                                    timeMode="12"
                                    minuteStep={30}
                                    time={scheduleDayForm[index].end_time}
                                    onTimeChange={(options) => {
                                      const { hour, minute, meridiem } =
                                        options;
                                      changeScheduleDayHandler(
                                        index,
                                        "end_time",
                                        `${hour}:${minute} ${meridiem}`
                                      );
                                    }}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>

              {account?.merchant?.parent_merchant_id > 0 && (
                <div class="row-fluid other_input" style={{ display: "none" }}>
                  <h4 class="sm-hd"> Menu Inheritance </h4>
                  <p>
                    {" "}
                    Select the Menu Items from the Parent Account (if available)
                    and whether you wish to maintain control at the Parent
                    Account (Master &amp; Lock) or within this account (Push
                    &amp; Forget). Any related Recipes, Ingredients, and Payees
                    will be auto-inherited as well.{" "}
                  </p>

                  <div class="menu-inheritance newForm_sec">
                    <ul class="ingredient-info edit-shift">
                      <li>
                        <label> </label>
                        <label class="span4">
                          {" "}
                          <sup
                            data-placement="bottom"
                            data-toggle="tooltip"
                            data-original-title="Tooltip"
                          >
                            {" "}
                            ?
                          </sup>
                          Master &amp; Lock{" "}
                        </label>
                        <label class="span4">
                          {" "}
                          <sup
                            data-placement="bottom"
                            data-toggle="tooltip"
                            data-original-title="Tooltip"
                          >
                            {" "}
                            ?
                          </sup>
                          Push &amp; Forget{" "}
                        </label>
                      </li>
                      <li>
                        <label class=""> Menu Category </label>
                        <div class="input-group span4 select-div search-select-ms select-width">
                          <Select
                            className="w-100"
                            options={[]}
                            value={[]}
                            isMulti
                          />
                        </div>

                        <div class="input-group span4 select-div search-select-ms select-width">
                          <Select
                            className="w-100"
                            options={[]}
                            value={[]}
                            isMulti
                          />
                        </div>
                      </li>
                      <li>
                        <label class=""> Menu Sub-Category </label>
                        <div class="input-group span4 select-div search-select-ms select-width">
                          <Select
                            className="w-100"
                            options={[]}
                            value={[]}
                            isMulti
                          />
                        </div>
                        <div class="input-group span4 select-div search-select-ms select-width">
                          <Select
                            className="w-100"
                            options={[]}
                            value={[]}
                            isMulti
                          />
                        </div>
                      </li>
                      <li>
                        <label class=""> Menu Items </label>
                        <div class="input-group span4 select-div search-select-ms select-width">
                          <Select
                            className="w-100"
                            options={[]}
                            value={[]}
                            isMulti
                          />
                        </div>
                        <div class="input-group span4 select-div search-select-ms select-width">
                          <Select
                            className="w-100"
                            options={[]}
                            value={[]}
                            isMulti
                          />
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              <button
                type="submit"
                style={{ display: "none" }}
                ref={submitBtnRef}
              >
                Submit
              </button>
              <BtmButtons submitHandler={submitNextHandler} />
            </form>
          </div>
        </div>
      </div>

      <ModelContainer
        title={`${
          accInputForm?.ap_id !== "" ? "Edit" : "Add"
        } Accounting Period`}
        open={accountingModal}
        closeModal={closeModal}
        submitModal={accountingSubmit(submitAccountingForm)}
        is_loading={is_modal_loading}
      >
        <form>
          <input
            type="hidden"
            name="is_first_period"
            {...register("is_first_period")}
            value={accInputForm?.is_first_period}
          />
          <input
            type="hidden"
            name="ap_id"
            {...register("ap_id")}
            value={accInputForm?.ap_id}
          />
          {console.log("accErrors", accErrors)}
          <div className="form-list">
            <ul className="px-0">
              <li>
                <label className="required"> Tax Year </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("tax_year")}
                    name="tax_year"
                    className="required changing tax_year_class"
                    onChange={(event) =>
                      changeAccInputHandler("tax_year", event?.target?.value)
                    }
                    value={accInputForm?.tax_year}
                  >
                    <option value=""> Select Tax Year </option>
                    {tenYearsBetween &&
                      tenYearsBetween !== null &&
                      accInputForm?.ap_id == "" &&
                      Object.keys(tenYearsBetween).map((index) => {
                        return (
                          <option value={index} key={index}>
                            {" "}
                            {tenYearsBetween?.[index]}{" "}
                          </option>
                        );
                      })}
                    {accInputForm?.ap_id !== "" && (
                      <option value={accInputForm?.tax_year}>
                        {accInputForm?.tax_year}
                      </option>
                    )}
                  </select>
                  <ErrorContainer errors={accErrors} name="tax_year" />
                </div>
              </li>

              <li>
                <label className="required"> Account Period Setting </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("account_period_setting_id")}
                    name="account_period_setting_id"
                    className="required changing tax_year_class"
                    onChange={(event) =>
                      changeAccInputHandler(
                        "account_period_setting_id",
                        event?.target?.value
                      )
                    }
                    value={accInputForm?.account_period_setting_id}
                  >
                    <option value=""> Select </option>
                    {accountPeriodSettings &&
                      accountPeriodSettings !== null &&
                      Object.keys(accountPeriodSettings).map((index) => {
                        return (
                          <option value={index} key={index}>
                            {" "}
                            {accountPeriodSettings?.[index]}{" "}
                          </option>
                        );
                      })}
                  </select>
                  <ErrorContainer
                    errors={accErrors}
                    name="account_period_setting_id"
                  />
                </div>
              </li>

              <li>
                <label className="required"> Sub Period </label>
                <div className="input-wrap select-div">
                  <select
                    {...register("sub_period")}
                    name="sub_period"
                    className="required changing tax_year_class"
                    onChange={(event) =>
                      changeAccInputHandler("sub_period", event?.target?.value)
                    }
                    value={accInputForm?.sub_period}
                  >
                    <option value=""> Select </option>
                    {sub_period &&
                      sub_period !== null &&
                      Object.keys(sub_period).map((index) => {
                        return (
                          <option value={index} key={index}>
                            {" "}
                            {sub_period?.[index]}{" "}
                          </option>
                        );
                      })}
                  </select>
                  <ErrorContainer errors={accErrors} name="sub_period" />
                </div>
              </li>

              {accInputForm?.account_period_setting_id &&
                accInputForm?.account_period_setting_id !== "" &&
                [1, 2, 3].indexOf(
                  parseInt(accInputForm?.account_period_setting_id)
                ) != -1 && (
                  <li class="settingsLi">
                    <label for="AccountingPeriodDayOfWeek" class="required">
                      Setting Type
                    </label>
                    <div class="input-wrap select-div">
                      <select
                        {...register("day_of_week")}
                        name="day_of_week"
                        class="required"
                        id="AccountingPeriodDayOfWeek"
                        onChange={(event) =>
                          changeAccInputHandler(
                            "day_of_week",
                            event?.target?.value
                          )
                        }
                        value={accInputForm?.day_of_week}
                      >
                        <option value="">Select Setting Type</option>
                        {settingTypes &&
                          settingTypes !== null &&
                          Object.keys(settingTypes).map((index) => {
                            return (
                              <option value={index} key={index}>
                                {" "}
                                {settingTypes?.[index]}{" "}
                              </option>
                            );
                          })}
                      </select>
                      <ErrorContainer errors={accErrors} name="day_of_week" />
                    </div>
                  </li>
                )}

              <li>
                <label className="required"> Period Start </label>
                <div className="input-wrap">
                  {/* <input
                    type="text"
                    name="start_date"
                    className="datepicker"
                  ></input> */}
                  <DatePicker
                    {...register("start_date")}
                    name="start_date"
                    autoComplete="new-password"
                    dateFormat="yyyy-MM-dd"
                    onChange={(val) => changeAccInputHandler("start_date", val)}
                    selected={accInputForm?.start_date}
                  />
                  {/* <span class="add-on">
                    <i class="fa fa-calendar"></i>
                  </span> */}
                </div>
                <ErrorContainer errors={accErrors} name="start_date" />
              </li>

              <li>
                <label className="required"> Period End</label>
                <div className="input-wrap">
                  {/* <input
                    type="text"
                    name="end_date"
                    className="datepicker"
                  ></input> */}
                  <DatePicker
                    {...register("end_date")}
                    name="end_date"
                    autoComplete="new-password"
                    dateFormat="yyyy-MM-dd"
                    onChange={(val) => changeAccInputHandler("end_date", val)}
                    selected={accInputForm?.end_date}
                    readonly
                    disabled
                  />
                  {/* <span class="add-on">
                    <i class="fa fa-calendar"></i>
                  </span> */}
                </div>
                <ErrorContainer errors={accErrors} name="end_date" />
              </li>
            </ul>
          </div>
        </form>
      </ModelContainer>
    </SWInterface>
  );
};
export default SetupWizardThree;
